import { useState, useEffect, useCallback } from 'react';
import { safeJson, networkMessage } from '../apiHelpers';
import PiPrice from './PiPrice';
import { t } from '../i18n';

const API_BASE = '/api/nft';

function getAuthToken() {
  return localStorage.getItem('token') || '';
}

function authHeaders() {
  const token = getAuthToken();
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Token ${token}` } : {}) };
}

const colors = {
  bg: '#F7F5F0', surface: '#fff', border: '#E3E1D9', text: '#1F1E1B',
  textMuted: '#5F5E5A', primary: '#0F6E56', primaryBg: '#E1F5EE', danger: '#A32D2D',
};

function NFTHelpModal({ onClose }) {
  const sectionStyle = { marginBottom: 20 };
  const headingStyle = { fontSize: 16, fontWeight: 600, margin: '0 0 8px' };
  const textStyle = { fontSize: 14, color: '#3A3935', lineHeight: 1.6, margin: '0 0 8px' };
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px 12px', overflowY: 'auto' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, maxWidth: 640, width: '100%', padding: '1.5rem', marginTop: 20 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 20 }}>{t('nfh_title')}</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: colors.textMuted }}>✕</button>
        </div>
        <div style={sectionStyle}>
          <p style={headingStyle}>{t('flh_what_q')}</p>
          <p style={textStyle}>{t('nfh_what')}</p>
        </div>
        <div style={sectionStyle}>
          <p style={headingStyle}>{t('nfh_mint_q')}</p>
          <p style={textStyle}>{t('nfh_m1')}</p>
          <p style={textStyle}>{t('nfh_m2')}</p>
          <p style={textStyle}>{t('nfh_m3')}</p>
        </div>
        <div style={sectionStyle}>
          <p style={headingStyle}>{t('nfh_buy_q')}</p>
          <p style={textStyle}>{t('nfh_buy')}</p>
        </div>
        <div style={sectionStyle}>
          <p style={headingStyle}>{t('nfh_fees_q')}</p>
          <p style={textStyle}>{t('nfh_fees')}</p>
        </div>
        <div style={sectionStyle}>
          <p style={{ ...headingStyle, color: colors.danger }}>{t('nfh_note_q')}</p>
          <p style={textStyle}>{t('nfh_note')}</p>
        </div>
        <button onClick={onClose} style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('got_it')}</button>
      </div>
    </div>
  );
}

const RARITY_CONFIG = {
  common: { label: 'Common', bg: '#E5E7EB', color: '#374151' },
  rare: { label: 'Rare', bg: '#DBEAFE', color: '#1D4ED8' },
  epic: { label: 'Epic', bg: '#EDE9FE', color: '#185FA5' },
  legendary: { label: 'Legendary', bg: '#d1fae5', color: '#991B1B' },
};

function RarityBadge({ rarity }) {
  const cfg = RARITY_CONFIG[rarity] || RARITY_CONFIG.common;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3, background: cfg.bg, color: cfg.color, padding: '2px 8px', borderRadius: 8, display: 'inline-block' }}>
      {rarity === 'legendary' ? '✨ ' : ''}{cfg.label}
    </span>
  );
}

function ItemCard({ item, onOpen }) {
  return (
    <div onClick={() => onOpen(item.id)} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 12, overflow: 'hidden', cursor: 'pointer' }}>
      <div style={{ width: '100%', aspectRatio: '1', background: '#F1EFE8', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        {item.image ? <img src={item.image} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 32, opacity: 0.3 }}>🖼️</span>}
        <div style={{ position: 'absolute', top: 6, left: 6 }}><RarityBadge rarity={item.rarity} /></div>
      </div>
      <div style={{ padding: '0.65rem' }}>
        <p style={{ fontSize: 12, color: colors.textMuted, margin: '0 0 2px' }}>{item.collection_name}</p>
        <p style={{ fontSize: 13, fontWeight: 500, margin: '0 0 4px' }}>{item.title}</p>
        <p style={{ fontSize: 11, color: colors.textMuted, margin: '0 0 4px' }}>#{item.edition_number}/{item.total_editions} · @{item.owner_username}</p>
        <p style={{ fontSize: 14, fontWeight: 600, color: colors.primary, margin: 0 }}><PiPrice pi={item.price_pi} /></p>
      </div>
    </div>
  );
}

function ItemDetailModal({ itemId, onClose, onChanged }) {
  const [item, setItem] = useState(null);
  const [buying, setBuying] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    fetch(`${API_BASE}/items/${itemId}/`, { headers: getAuthToken() ? authHeaders() : {} }).then((r) => r.json()).then(setItem).catch(() => {});
  }, [itemId]);

  useEffect(() => { load(); }, [load]);

  const buy = async () => {
    if (!getAuthToken()) { setMsg(t('login_first')); return; }
    if (!window.Pi) { setMsg(t('open_pi_pay')); return; }
    setBuying(true);
    setMsg('');
    try {
      const res = await fetch(`${API_BASE}/items/${itemId}/buy/`, { method: 'POST', headers: authHeaders() });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not start purchase.');

      await window.Pi.authenticate(['username', 'payments'], () => {});
      window.Pi.createPayment(
        { amount: data.price_pi, memo: `Buy NFT: ${item.title}`, metadata: { transaction_id: data.transaction_id } },
        {
          onReadyForServerApproval: (paymentId) => fetch(`${API_BASE}/purchases/${data.transaction_id}/approve/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ payment_id: paymentId }) }),
          onReadyForServerCompletion: (paymentId, txid) => {
            fetch(`${API_BASE}/purchases/${data.transaction_id}/complete/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ payment_id: paymentId, txid }) })
              .then(() => { setMsg('Purchase complete - it\'s yours!'); setBuying(false); load(); onChanged(); })
              .catch(() => { setMsg('Payment reached Pi but transfer failed.'); setBuying(false); });
          },
          onCancel: () => { setMsg(t('pay_cancelled')); setBuying(false); },
          onError: () => { setMsg(t('pay_error')); setBuying(false); },
        }
      );
    } catch (e) { setMsg(e.message); setBuying(false); }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px 12px', overflowY: 'auto' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, maxWidth: 500, width: '100%', padding: '1.5rem', marginTop: 20 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: colors.textMuted }}>✕</button>
        </div>
        {!item ? <p style={{ textAlign: 'center', color: colors.textMuted }}>{t('loading')}</p> : (
          <>
            {item.image && <img src={item.image} alt={item.title} style={{ width: '100%', borderRadius: 10, marginBottom: 12 }} />}
            <p style={{ fontSize: 12, color: colors.primary, margin: '0 0 4px' }}>{item.collection_name}</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <h2 style={{ fontSize: 18, margin: 0 }}>{item.title}</h2>
              <RarityBadge rarity={item.rarity} />
            </div>
            <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 12px' }}>#{item.edition_number}/{item.total_editions} · Created by @{item.creator_username} · Owned by @{item.owner_username}</p>
            <p style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 16 }}>{item.description}</p>

            {item.is_listed && !item.is_owner && (
              <>
                <p style={{ fontSize: 16, fontWeight: 600, color: colors.primary, marginBottom: 12 }}><PiPrice pi={item.price_pi} /></p>
                <button
                  onClick={() => {
                    fetch('/api/cart/add/', {
                      method: 'POST', headers: authHeaders(),
                      body: JSON.stringify({
                        app_name: 'nft', item_type: 'item', reference_id: item.id,
                        title: item.title, thumbnail_url: item.image || '', price_pi: item.price_pi, metadata: {},
                      }),
                    }).then(() => setMsg('Added to cart! View it from the 🛒 My Cart menu.'));
                  }}
                  style={{ width: '100%', padding: '12px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}
                >
                  🛒 Add to Cart - <PiPrice pi={item.price_pi} />
                </button>
              </>
            )}
            {!item.is_listed && !item.is_owner && <p style={{ fontSize: 13, color: colors.textMuted }}>{t('not_for_sale')}</p>}
            {msg && <p style={{ fontSize: 12, color: colors.danger, marginTop: 8 }}>{msg}</p>}

            {item.history?.length > 0 && (
              <div style={{ marginTop: 16, borderTop: `0.5px solid ${colors.border}`, paddingTop: 12 }}>
                <p style={{ fontSize: 13, fontWeight: 600, margin: '0 0 8px' }}>{t('ownership_history')}</p>
                {item.history.map((h, i) => (
                  <p key={i} style={{ fontSize: 12, color: colors.textMuted, margin: '4px 0' }}>@{h.seller} → @{h.buyer} for <PiPrice pi={h.price_pi} /></p>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function MintForm({ onMinted }) {
  const [collections, setCollections] = useState([]);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [form, setForm] = useState({ collection_id: '', title: '', description: '', rarity: 'common', edition_number: 1, total_editions: 1 });
  const [imageFile, setImageFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [hasBlueTick, setHasBlueTick] = useState(false);
  const [autoPost, setAutoPost] = useState(false);

  useEffect(() => {
    fetch('/api/blue-tick/', { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => setHasBlueTick(!!d.has_blue_tick))
      .catch(() => {});
  }, []);

  const loadCollections = useCallback(async () => {
    const res = await fetch(`${API_BASE}/collections/mine/`, { headers: authHeaders() });
    if (res.ok) {
      const data = await safeJson(res);
      setCollections(data.collections);
      if (data.collections.length && !form.collection_id) setForm((f) => ({ ...f, collection_id: data.collections[0].id }));
    }
  }, [form.collection_id]);

  useEffect(() => { loadCollections(); }, [loadCollections]);

  const createCollection = async () => {
    if (!newCollectionName) return;
    const res = await fetch(`${API_BASE}/collections/create/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ name: newCollectionName }) });
    if (res.ok) { setNewCollectionName(''); loadCollections(); }
  };

  const mint = async () => {
    if (!form.collection_id) { alert('Create a collection first.'); return; }
    if (!imageFile) { alert('Please choose an image.'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append('image', imageFile);
      fd.append('auto_post', autoPost ? 'true' : 'false');
      const token = getAuthToken();
      const res = await fetch(`${API_BASE}/items/mint/`, { method: 'POST', headers: token ? { Authorization: `Token ${token}` } : {}, body: fd });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not mint.');
      alert('Minted!');
      setForm({ ...form, title: '', description: '', rarity: 'common', edition_number: 1, total_editions: 1 });
      setImageFile(null);
      onMinted();
    } catch (e) { alert(networkMessage(e)); } finally { setSubmitting(false); }
  };

  const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 14, border: '0.5px solid #B4B2A9', borderRadius: 6, boxSizing: 'border-box' };
  const labelStyle = { fontSize: 13, color: colors.textMuted, display: 'block', marginBottom: 4 };

  return (
    <div style={{ background: '#F1EFE8', border: `0.5px solid ${colors.border}`, borderRadius: 12, padding: '1.25rem' }}>
      <p style={{ fontWeight: 500, fontSize: 15, margin: '0 0 12px' }}>{t('mint_new_item')}</p>

      {collections.length === 0 ? (
        <div style={{ marginBottom: 12 }}>
          <p style={{ fontSize: 13, color: colors.textMuted, marginBottom: 8 }}>{t('nft_coll_first')}</p>
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={inputStyle} placeholder={t('collection_name')} value={newCollectionName} onChange={(e) => setNewCollectionName(e.target.value)} />
            <button onClick={createCollection} style={{ padding: '0 16px', fontSize: 13, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('create_btn')}</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          <div><label style={labelStyle}>{t('collection_label')}</label>
            <select style={inputStyle} value={form.collection_id} onChange={(e) => setForm((f) => ({ ...f, collection_id: e.target.value }))}>
              {collections.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div><label style={labelStyle}>{t('title_label')}</label><input style={inputStyle} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} /></div>
          <div><label style={labelStyle}>{t('description')}</label><textarea style={{ ...inputStyle, resize: 'vertical' }} rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>
          <div><label style={labelStyle}>{t('rarity_label')}</label>
            <select style={inputStyle} value={form.rarity} onChange={(e) => setForm((f) => ({ ...f, rarity: e.target.value }))}>
              <option value="common">{t('common_rarity')}</option>
              <option value="rare">{t('rare_rarity')}</option>
              <option value="epic">{t('epic_rarity')}</option>
              <option value="legendary">{t('nft_legendary')}</option>
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><label style={labelStyle}>{t('nft_edition')}</label><input type="number" style={inputStyle} value={form.edition_number} onChange={(e) => setForm((f) => ({ ...f, edition_number: e.target.value }))} /></div>
            <div><label style={labelStyle}>{t('total_editions')}</label><input type="number" style={inputStyle} value={form.total_editions} onChange={(e) => setForm((f) => ({ ...f, total_editions: e.target.value }))} /></div>
          </div>
          <div><label style={labelStyle}>{t('image_label')}</label><input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} /></div>
        </div>
      )}
      {hasBlueTick && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
          <input type="checkbox" id="nft-autopost" checked={autoPost} onChange={(e) => setAutoPost(e.target.checked)} />
          <label htmlFor="nft-autopost" style={{ fontSize: 13, margin: 0 }}>{t('auto_post_perk')}</label>
        </div>
      )}
      <button onClick={mint} disabled={submitting || collections.length === 0 || !form.title} style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer', marginTop: 12 }}>
        {submitting ? 'Minting...' : 'Mint item'}
      </button>
    </div>
  );
}

function MyItemsPanel({ onOpen }) {
  const [items, setItems] = useState(null);
  const [priceInputs, setPriceInputs] = useState({});

  const load = useCallback(async () => {
    const res = await fetch(`${API_BASE}/items/mine/`, { headers: authHeaders() });
    if (res.ok) setItems((await safeJson(res)).items);
  }, []);

  useEffect(() => { load(); }, [load]);

  const listForSale = async (id) => {
    const price = priceInputs[id];
    if (!price || price <= 0) { alert('Enter a valid price.'); return; }
    await fetch(`${API_BASE}/items/${id}/list/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ price_pi: price }) });
    load();
  };

  const unlist = async (id) => {
    await fetch(`${API_BASE}/items/${id}/unlist/`, { method: 'POST', headers: authHeaders() });
    load();
  };

  return (
    <div>
      {items && items.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: '1.5rem' }}>
          {items.map((i) => (
            <div key={i.id} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: 8 }}>
              <div onClick={() => onOpen(i.id)} style={{ width: '100%', aspectRatio: '1', background: '#F1EFE8', borderRadius: 6, marginBottom: 6, overflow: 'hidden', cursor: 'pointer' }}>
                {i.image && <img src={i.image} alt={i.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
              </div>
              <p style={{ fontSize: 12, fontWeight: 500, margin: '0 0 4px' }}>{i.title}</p>
              <div style={{ marginBottom: 6 }}><RarityBadge rarity={i.rarity} /></div>
              {i.is_listed ? (
                <>
                  <p style={{ fontSize: 12, color: colors.primary, margin: '0 0 4px' }}>{t('nft_listed')} <PiPrice pi={i.price_pi} suffix="" /></p>
                  <button onClick={() => unlist(i.id)} style={{ width: '100%', padding: '4px', fontSize: 11, border: `0.5px solid ${colors.border}`, borderRadius: 4, background: '#fff', cursor: 'pointer' }}>{t('unlist_btn')}</button>
                </>
              ) : (
                <div style={{ display: 'flex', gap: 4 }}>
                  <input type="number" placeholder={t('price_label')} style={{ flex: 1, padding: '4px', fontSize: 11, border: `0.5px solid ${colors.border}`, borderRadius: 4, width: 0 }} onChange={(e) => setPriceInputs((p) => ({ ...p, [i.id]: e.target.value }))} />
                  <button onClick={() => listForSale(i.id)} style={{ padding: '4px 8px', fontSize: 11, border: 'none', borderRadius: 4, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('list_btn')}</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <MintForm onMinted={load} />
    </div>
  );
}

function WalletPanel() {
  const [balance, setBalance] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [showLedger, setShowLedger] = useState(false);
  const [amount, setAmount] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    const res = await fetch(`${API_BASE}/wallet/balance/`, { headers: authHeaders() });
    if (res.ok) setBalance(await safeJson(res));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleLedger = async () => {
    if (!showLedger) {
      const res = await fetch(`${API_BASE}/wallet/ledger/`, { headers: authHeaders() });
      if (res.ok) setLedger((await safeJson(res)).entries);
    }
    setShowLedger((s) => !s);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/wallet/withdraw/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ amount: parseFloat(amount), pi_wallet_address: walletAddress }) });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not submit request.');
      alert(t('msg_wd_submitted'));
      setAmount(''); setWalletAddress('');
      load();
    } catch (e) { alert(networkMessage(e)); } finally { setSubmitting(false); }
  };

  if (balance === null) return <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('loading_wallet')}</p>;

  return (
    <div>
      <div style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 12, padding: '1rem', marginBottom: '1.5rem' }}>
        <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 4px' }}>{t('available_to_withdraw')}</p>
        <p style={{ fontSize: 26, fontWeight: 600, margin: '0 0 4px', color: colors.primary }}><PiPrice pi={balance.available_balance_pi} /></p>
        <p style={{ fontSize: 12, color: colors.textMuted, margin: '0 0 8px' }}>{t('earned_after_fee')} <PiPrice pi={balance.total_earned_pi} /></p>
        <button onClick={toggleLedger} style={{ fontSize: 12, padding: '4px 10px', border: `0.5px solid ${colors.border}`, borderRadius: 6, background: '#fff', cursor: 'pointer' }}>{showLedger ? 'Hide' : 'View'} ledger</button>
        {showLedger && (
          <div style={{ marginTop: 10, borderTop: `0.5px solid ${colors.border}`, paddingTop: 10 }}>
            {ledger.length === 0 ? <p style={{ fontSize: 12, color: colors.textMuted }}>{t('no_sales_yet')}</p> :
              ledger.map((e, i) => (
                <div key={i} style={{ fontSize: 12, padding: '6px 0', borderBottom: `0.5px solid ${colors.border}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>{e.item_title} · @{e.buyer}</span>
                    <span><PiPrice pi={e.net_pi} /> net</span>
                  </div>
                </div>
              ))
            }
          </div>
        )}
      </div>
      <div style={{ background: '#F1EFE8', border: `0.5px solid ${colors.border}`, borderRadius: 12, padding: '1.25rem' }}>
        <p style={{ fontWeight: 500, fontSize: 14, margin: '0 0 12px' }}>{t('request_withdrawal')}</p>
        <div style={{ display: 'grid', gap: 10, marginBottom: 10 }}>
          <input type="number" step="0.000001" placeholder={t('amount_pi')} value={amount} onChange={(e) => setAmount(e.target.value)} style={{ padding: '8px 10px', fontSize: 14, border: '0.5px solid #B4B2A9', borderRadius: 6 }} />
          <input type="text" placeholder={t('your_wallet_address')} value={walletAddress} onChange={(e) => setWalletAddress(e.target.value)} style={{ padding: '8px 10px', fontSize: 14, border: '0.5px solid #B4B2A9', borderRadius: 6 }} />
        </div>
        <button onClick={submit} disabled={submitting || !amount || !walletAddress} style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer' }}>
          {submitting ? 'Submitting...' : 'Request withdrawal'}
        </button>
      </div>
    </div>
  );
}

export default function NFTApp() {
  const [tab, setTab] = useState('browse');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [rarityFilter, setRarityFilter] = useState('');
  const [openItemId, setOpenItemId] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (rarityFilter) params.set('rarity', rarityFilter);
      const res = await fetch(`${API_BASE}/marketplace/?${params.toString()}`);
      const data = await safeJson(res);
      setItems(data.items || []);
    } catch (e) {} finally { setLoading(false); }
  }, [search, rarityFilter]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const tabStyle = (t) => ({ padding: '8px 14px', fontSize: 13, fontWeight: 500, border: 'none', borderBottom: tab === t ? `2px solid ${colors.primary}` : '2px solid transparent', background: 'transparent', color: tab === t ? colors.primary : colors.textMuted, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' });

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '1.5rem', fontFamily: 'sans-serif', background: colors.bg, minHeight: '100vh' }}>
      {showHelp && <NFTHelpModal onClose={() => setShowHelp(false)} />}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ fontSize: 22, margin: '0 0 4px' }}>{t('nft_app')}</h1>
          <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 1rem' }}>{t('nft_tagline')}</p>
        </div>
        <button onClick={() => setShowHelp(true)} style={{ padding: '6px 14px', fontSize: 13, fontWeight: 500, border: `0.5px solid ${colors.border}`, borderRadius: 20, background: '#fff', cursor: 'pointer' }}>{t('how_it_works_btn')}</button>
      </div>

      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', borderBottom: `0.5px solid ${colors.border}`, marginBottom: '1.25rem' }}>
        <button style={tabStyle('browse')} onClick={() => setTab('browse')}>{t('marketplace_tab')}</button>
        <button style={tabStyle('myitems')} onClick={() => setTab('myitems')}>{t('my_items')}</button>
        <button style={tabStyle('wallet')} onClick={() => setTab('wallet')}>{t('wallet')}</button>
      </div>

      {tab === 'browse' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: '0.75rem' }}>
            <input placeholder={t('nft_search_ph')} value={search} onChange={(e) => setSearch(e.target.value)} style={{ flex: 1, padding: '10px 14px', fontSize: 14, border: `0.5px solid #B4B2A9`, borderRadius: 20, boxSizing: 'border-box' }} />
            <button onClick={loadItems} style={{ padding: '0 14px', fontSize: 13, border: `0.5px solid ${colors.border}`, borderRadius: 20, background: '#fff', cursor: 'pointer' }}>🔄</button>
          </div>
          <div style={{ display: 'flex', gap: 6, marginBottom: '0.75rem', flexWrap: 'wrap' }}>
            {['', 'common', 'rare', 'epic', 'legendary'].map((r) => (
              <button key={r} onClick={() => setRarityFilter(r)} style={{ padding: '5px 12px', fontSize: 12, borderRadius: 14, border: `0.5px solid ${colors.border}`, cursor: 'pointer', background: rarityFilter === r ? colors.primary : '#fff', color: rarityFilter === r ? '#fff' : colors.textMuted }}>
                {r === '' ? 'All' : RARITY_CONFIG[r].label}
              </button>
            ))}
          </div>
          {loading ? <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('loading')}</p> :
            items.length === 0 ? <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('no_items_listed')}</p> : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
                {items.map((i) => <ItemCard key={i.id} item={i} onOpen={setOpenItemId} />)}
              </div>
            )
          }
          {openItemId && <ItemDetailModal itemId={openItemId} onClose={() => setOpenItemId(null)} onChanged={loadItems} />}
        </>
      )}

      {tab === 'myitems' && <MyItemsPanel onOpen={setOpenItemId} />}
      {tab === 'wallet' && <WalletPanel />}

      {tab !== 'browse' && openItemId && <ItemDetailModal itemId={openItemId} onClose={() => setOpenItemId(null)} onChanged={() => {}} />}
    </div>
  );
}
